import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import DropdownSelect from "../../FamilyTree/DropdownSelect";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { putApi, getApi } from "../../../Services/apiService";
import { convertDateToTimestamp } from "../../../Utils/dateUtils";
import {
  getFamilyTreeIdFromStorage,
  setFamilyTreeIdInLoggedInRole,
} from "../../../Utils/headOfLineRole";
import { isLoggedInUserTreeAdminFromStorage } from "../../../Utils/subscriptionAccess";
import {
  getFullNamesTopOfLineDisplay,
  getTopOfLineDisplayLines,
} from "../../../Utils/topOfLineFamilyTitle";
import { renderNameWithBoldFirst } from "../../../Utils/name";
import "../../../Auth.css";
import { states } from "../../../constants/states";
import { formatPhoneNumber } from "../../../Utils/phone";
import { AddMemberSpecialNotesField } from "../../../Modals/AddMemberComponents/AddMemberSpecialNotesField";
import "../../../popups.css";

function getProfileNameParts(user) {
  if (!user || typeof user !== "object") {
    return { firstName: "", lastName: "" };
  }

  const name = (user.name || "").trim();
  const firstFromUser = (user.first_name || user.firstName || "").trim();
  const lastFromUser = (user.last_name || user.lastName || "").trim();

  if (firstFromUser || lastFromUser) {
    let firstName = firstFromUser;
    let lastName = lastFromUser;

    if (!firstName || !lastName) {
      const nameParts = name.split(/\s+/).filter(Boolean);
      if (nameParts.length > 1) {
        if (!firstName) firstName = nameParts.slice(0, -1).join(" ");
        if (!lastName) lastName = nameParts[nameParts.length - 1];
      } else if (nameParts.length === 1 && !firstName) {
        firstName = nameParts[0];
      }
    }

    return { firstName, lastName };
  }

  const nameParts = name.split(/\s+/).filter(Boolean);
  if (nameParts.length > 1) {
    return {
      firstName: nameParts.slice(0, -1).join(" "),
      lastName: nameParts[nameParts.length - 1],
    };
  }
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: "" };
  }
  return { firstName: "", lastName: "" };
}

const MyProfile = () => {
  const [user, setUser] = useState({});
  const [isEditable, setIsEditable] = useState(false);
  const [headOfLineData, setHeadOfLineData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(() => isLoggedInUserTreeAdminFromStorage());
  const [topOfLineTitle, setTopOfLineTitle] = useState("");
  // Mobile only: Start/Top of the Line rendered like the tree's gen-1 node
  // (member line + spouse line). Desktop keeps the single-line title.
  const [topOfLineLines, setTopOfLineLines] = useState([]);
  const topOfLineRef = useRef(null);

  /* Desktop keeps the whole title on one line, whatever its length: it starts at
     the 18px the design calls for and steps down only as far as it must to fit
     the box. Mobile has its own wrapped format, so the size is left to the CSS
     there. */
  useLayoutEffect(() => {
    const box = topOfLineRef.current;
    if (!box) return undefined;

    const MAX_FONT_PX = 18;
    const MIN_FONT_PX = 10;

    const fit = () => {
      if (window.matchMedia("(max-width: 768px)").matches) {
        box.style.fontSize = "";
        return;
      }
      let size = MAX_FONT_PX;
      box.style.fontSize = `${size}px`;
      while (size > MIN_FONT_PX && box.scrollWidth > box.clientWidth) {
        size -= 0.5;
        box.style.fontSize = `${size}px`;
      }
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    window.addEventListener("resize", fit);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [topOfLineTitle, topOfLineLines]);

  useEffect(() => {
    const updateRoleFromStorage = () => {
      setIsAdmin(isLoggedInUserTreeAdminFromStorage());
      try {
        const role = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
        setTopOfLineTitle(getFullNamesTopOfLineDisplay(role.top_of_the_line));
        setTopOfLineLines(getTopOfLineDisplayLines(role.top_of_the_line));
      } catch {
        setTopOfLineTitle("");
        setTopOfLineLines([]);
      }
    };
    // init on mount
    updateRoleFromStorage();
    window.addEventListener("loggedInUserRoleUpdated", updateRoleFromStorage);
    window.addEventListener("focus", updateRoleFromStorage);
    return () => {
      window.removeEventListener("loggedInUserRoleUpdated", updateRoleFromStorage);
      window.removeEventListener("focus", updateRoleFromStorage);
    };
  }, []);

  // Form data — include optional fields with default values
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "",
    country: "",
    city: "",
    about: "",
    profilePic: "",
    profilePicPreview: "",
    maritalStatus: "",
    fileError: "",
    weddingAnniversary: "",
    relationToHeadOfLine: "",
    bioDescendantParent: "",
    familyLineColor: "",
    cellType: "",
  });

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
    const treeId = getFamilyTreeIdFromStorage();
    setUser({
      ...storedUser,
      ...(treeId ? { familyTreeId: treeId, family_tree_id: treeId } : {}),
    });
  }, []);

  // Fetch head of line data when user is loaded
  useEffect(() => {
    const fetchHeadOfLine = async () => {
      const treeId =
        getFamilyTreeIdFromStorage() ||
        user?.familyTreeId ||
        user?.family_tree_id;
      if (treeId) {
        try {
          const response = await getApi(`/head-of-line/${treeId}`);
          const liu = response?.data?.logged_in_user;
          if (response?.statusCode === 200 && liu) {
            if (liu.head_of_line) {
              setHeadOfLineData(liu.head_of_line);
            }
            setUser((prev) => {
              const fm = liu.family_member || liu.member || liu;
              const nextBio =
                liu.bio_descendant_parent ??
                fm.bio_descendant_parent ??
                prev.bio_descendant_parent;
              const nextFirst =
                fm.first_name || liu.first_name || prev.first_name;
              const nextLast =
                fm.last_name || liu.last_name || prev.last_name;
              if (
                nextBio === prev.bio_descendant_parent &&
                nextFirst === prev.first_name &&
                nextLast === prev.last_name
              ) {
                return prev;
              }
              return {
                ...prev,
                bio_descendant_parent: nextBio,
                first_name: nextFirst,
                last_name: nextLast,
              };
            });
          }
        } catch (error) {
          console.error("Error fetching head of line:", error);
        }
      }
    };

    if (
      Object.keys(user).length > 0 &&
      (getFamilyTreeIdFromStorage() || user.familyTreeId || user.family_tree_id)
    ) {
      fetchHeadOfLine();
    }
  }, [user]);

  // Update form data when user is loaded
  useEffect(() => {
    if (Object.keys(user).length > 0) {
      const {
        email = "",
        phone_number = "",
        country_code = "",
        profileImage = "",
        marital_status = "",
        relation_to_head_of_line = "",
        bio_descendant_parent = "",
        family_line_color = "",
        phone_type = "",
      } = user;

      const { firstName, lastName } = getProfileNameParts(user);

      setFormData({
        firstName,
        lastName,
        email,
        phone: phone_number || "",
        countryCode: country_code || "",
        country: user.country || "",
        city: user.city || "",
        about: user.about || "",
        profilePic: profileImage || "",
        profilePicPreview: "",
        maritalStatus: marital_status || "",
        fileError: "",
        relationToHeadOfLine: relation_to_head_of_line || "",
        bioDescendantParent: bio_descendant_parent || "",
        familyLineColor: family_line_color || "",
        cellType: phone_type || "",
      });
    }
  }, [user]);

  const [errors, setErrors] = useState({
    firstName: "",
    maritalStatus: "",
  });

  //Form Validation
  const validateForm = () => {
  let newErrors = {};
  let firstInvalidField = null;

  const addError = (field, message) => {
    newErrors[field] = message;
    if (!firstInvalidField) firstInvalidField = field;
  };

  // First Name
  if (!formData.firstName.trim()) addError("firstName", "First name is required.");

  // Last Name
  if (!formData.lastName.trim()) addError("lastName", "Last name is required.");

  // Phone
  if (!formData.phone.trim()) addError("phone", "Phone number is required.");

  // Cell Type
  if (!formData.cellType) addError("cellType", "Please Select cell type.");

  // State
  if (!formData.city) addError("city", "Please select your state.");

  // Country
  if (!formData.country) addError("country", "Please select your country.");

  // Marital Status
  // if (!formData.maritalStatus)
  //   addError("maritalStatus", "Please select your marital status.");

  // Wedding Anniversary Required ONLY if Married (2)
  if (formData.maritalStatus === 2) {
    if (!formData.weddingAnniversary) {
      addError("weddingAnniversary", "Please select your wedding anniversary.");
    }
  }

  setErrors(newErrors);

  // Focus first error field
  if (firstInvalidField) {
    document.querySelector(`[name="${firstInvalidField}"]`)?.focus();
  }

  return Object.keys(newErrors).length === 0;
};


  const maritalStatusOptions = [
    { value: 1, label: "Single/Divorced" },
    { value: 3, label: "In Relationship" },
    { value: 2, label: "Married" },
    { value: 5, label: "Separated" },
    { value: 6, label: "Remarried" },
    { value: 4, label: "Widowed" },
  ];
  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Allowed image formats
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

    // If invalid file type → just skip (don’t set anything)
    if (!allowedTypes.includes(file.type)) {
      setFormData((prev) => ({
        ...prev,
        profilePic: null,
        profilePicPreview: "",
        fileError: "Only JPEG, JPG, and PNG formats are allowed.",
      }));
      return;
    }

    // If valid → generate preview and clear error
    const imageUrl = URL.createObjectURL(file);

    setFormData((prev) => ({
      ...prev,
      profilePic: file, // real file for backend
      profilePicPreview: imageUrl, // preview for UI
      fileError: "", // clear any previous error
    }));
  };

  // Handle changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    //Update the error state
    setErrors((prev) => ({
      ...prev,
      [name]: ""
    }))
  };

  //Handle save
  const handleSave = async () => {
    try {
      if (!validateForm()) {
        return;
      }
      console.log("Updated data:", formData);
      const formDataToSend = new FormData();

      // Helper function to check if value exists
      const hasValue = (value) => {
        return value !== null && value !== undefined && value !== "" && String(value).trim() !== "";
      };

      // Append name (required field from firstName and lastName)
      if (hasValue(formData.firstName) || hasValue(formData.lastName)) {
        formDataToSend.append(
          "name",
          `${formData.firstName || ""} ${formData.lastName || ""}`.trim()
        );
      }

      // Append email if it has value
      if (hasValue(formData.email)) {
        formDataToSend.append("email", formData.email);
      }

      // Append optional fields only if they have values
      if (hasValue(formData.about)) {
        formDataToSend.append("about", formData.about);
      }
      if (hasValue(formData.city)) {
        formDataToSend.append("city", formData.city);
      }
      if (hasValue(formData.country)) {
        formDataToSend.append("country", formData.country);
      }
      if (hasValue(formData.countryCode)) {
        formDataToSend.append("country_code", formData.countryCode);
      }
      if (hasValue(formData.phone)) {
        formDataToSend.append("phone_number", formData.phone);
      }
      if (hasValue(formData.maritalStatus)) {
        formDataToSend.append("marital_status", formData.maritalStatus);
      }
      if (hasValue(formData.weddingAnniversary)) {
        formDataToSend.append(
          "wedding_anniversary",
          convertDateToTimestamp(formData.weddingAnniversary)
        );
      }
      if (hasValue(formData.relationToHeadOfLine)) {
        formDataToSend.append("relation_to_head_of_line", formData.relationToHeadOfLine);
      }
      if (hasValue(formData.bioDescendantParent)) {
        formDataToSend.append("bio_descendant_parent", formData.bioDescendantParent);
      }
      if (hasValue(formData.familyLineColor)) {
        formDataToSend.append("family_line_color", formData.familyLineColor);
      }
      if (hasValue(formData.cellType)) {
        formDataToSend.append("phone_type", formData.cellType);
      }

      // Append image only if it's a File object
      if (formData.profilePic && formData.profilePic instanceof File) {
        formDataToSend.append("image", formData.profilePic);
      }

      const response = await putApi("/edit-profile", formDataToSend);
      console.log(response);

      if (response?.statusCode === 200) {
        toast.success(
          response?.message || "Profile updated successfully",
          formData
        );

        // Get existing localStorage data to preserve important fields
        const existingUserData = JSON.parse(localStorage.getItem("userDetail") || "{}");
        
        // Merge API response with existing localStorage data
        // Preserve fields that might not be in API response (like family_tree_id, token, etc.)
        const updatedUser = {
          ...existingUserData, // Keep existing data first
          ...response?.data,    // Override with new data from API
          token: existingUserData.token || response?.data?.token,
          id: existingUserData.id || response?.data?.id,
        };
        delete updatedUser.family_tree_id;
        delete updatedUser.familyTreeId;

        const treeFromResponse =
          response?.data?.family_tree_id || response?.data?.familyTreeId;
        if (treeFromResponse) {
          setFamilyTreeIdInLoggedInRole(String(treeFromResponse));
        }

        //Update the localStorage user data (family tree id lives on loggedIn_user_role)
        localStorage.setItem("userDetail", JSON.stringify(updatedUser));

        const treeId = getFamilyTreeIdFromStorage();
        setUser({
          ...updatedUser,
          ...(treeId ? { familyTreeId: treeId, family_tree_id: treeId } : {}),
        });

        // Trigger the event to update the name in the header
        window.dispatchEvent(new Event("userUpdated"));
        //Exit edit Mode
        setIsEditable(false);
      } else {
        // Show error if backend returns a non-200 response
        toast.error(
          response?.message || "Failed to update profile. Please try again."
        );
      }
    } catch (error) {
      console.error("Error updating profile:", error);

      // Safely handle all possible error shapes
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong while updating your profile.";

      toast.error(errorMessage);
    }
  };

  //Handle Phone Change
  const handlePhoneChange = (value, country) => {
    const dialCode = `+${country.dialCode}`; // e.g. "+91"
    const trimmedValue = value.trim();

    // Try to detect if the input already includes the country code
    const match = trimmedValue.match(/^(\+\d{1,3})\s*(.*)$/);

    const extractedCountryCode = match ? match[1] : dialCode;
    const phoneNumber = match
      ? match[2] || ""
      : trimmedValue.replace(country.dialCode, "").trim();

    setFormData((prev) => ({
      ...prev,
      countryCode: extractedCountryCode,
      phone: phoneNumber,
    }));
  };

  // Handle Cancel
  const handleCancel = () => {
    const {
      email = "",
      phone_number = "",
      country_code = "",
      profileImage = "",
      relation_to_head_of_line = "",
      bio_descendant_parent = "",
      family_line_color = "",
      phone_type = "",
    } = user;

    const { firstName, lastName } = getProfileNameParts(user);

    setFormData({
      firstName,
      lastName,
      email,
      phone: phone_number || "",
      countryCode: country_code || "",
      country: user.country || "",
      city: user.city || "",
      about: user.about || "",
      profilePic: profileImage || "",
      profilePicPreview: "",
      fileError: "",
      relationToHeadOfLine: relation_to_head_of_line || "",
      bioDescendantParent: bio_descendant_parent || "",
      familyLineColor: family_line_color || "",
      cellType: phone_type || "",
    });

    setErrors({
      firstName: "",
      maritalStatus: "",
    });
    setIsEditable(false);
  };
  return (
    <>
      <style>{`
        /* Less top padding + tighter gaps so profile fits without scrolling */
        .family-tree-container.settings-shell .right-side {
          padding: 12px 20px 16px;
        }
        /* Auth.css caps every form at 450px (popups.css only unsets it when it
           happens to load later). Opt this one out so the panel keeps its full
           width and the three-across grid can centre itself. */
        .seeting-profile form {
          width: 100%;
          max-width: 100%;
        }
        .seeting-profile .profile-header {
          margin-bottom: 8px;
        }
        .seeting-profile .profile-image-container .profile-pic {
          width: 88px;
          height: 88px;
        }
        .seeting-profile .profile-form {
          /* Row gap only — the global .form-group margin-bottom (1.5rem from
             Auth.css) is zeroed below so rows sit tight under each input. */
          gap: 8px 10px;
          margin-top: 32px;
        }
        .three-col-form .form-group label {
          margin-bottom: 4px;
        }
        .seeting-profile .profile-form.three-col-form {
          max-width: min(100%, 840px);
          margin-left: auto;
          margin-right: auto;
        }
        /* Desktop only: the form block keeps its 840px three-across format but
           sits flush left in the card instead of being centred, matching the
           left justification of the labels inside it. */
        @media (min-width: 769px) {
          .seeting-profile .profile-form.three-col-form {
            margin-left: 0;
            margin-right: auto;
          }
          /* Avatar sits flush left with the form block instead of centred,
             matching the left justification of the fields below it. */
          .seeting-profile .profile-header.text-center {
            text-align: left !important;
            justify-content: flex-start !important;
          }
          /* Edit button sits above the Head/Color column, flush with that
             field's right edge (the form block is 840px wide and flush left)
             and dropped to the bottom of the header, level with the avatar. */
          .seeting-profile .edit-button-cs {
            right: calc(100% - min(100%, 840px)) !important;
            left: auto;
            top: auto;
            bottom: 0;
          }
        }
        /* Edit button matches the Accept/Decline pair on Events: same label
           size and weight, the same 6px vertical padding and 4px radius (App.css
           otherwise makes this a 50px pill) and the same label-driven width.
           blueButtons.css sets 18px/700 !important, so these need !important to
           win. */
        .seeting-profile .edit-button-cs {
          /* Same 76px floor as the Accept/Decline buttons on Events (App.css),
             so Edit comes out the same width, rather than the fixed 120px
             App.css gives it. */
          width: auto !important;
          min-width: 76px;
          padding: 6px 10px !important;
          /* Buttons default to the browser's UI font; the rest of the app's
             buttons inherit the body face via Bootstrap's .btn (whose
             --bs-btn-font-family is empty, i.e. inherit). Match them, or the
             same 15px reads as a different size. */
          font-family: inherit;
          font-size: 15px !important;
          font-weight: 600 !important;
          line-height: 1.2 !important;
          border-radius: 4px !important;
        }
        .three-col-form .profile-meta-row {
          display: flex;
          flex-wrap: nowrap;
          gap: 10px;
          width: 100%;
          flex: 1 1 100%;
          align-items: flex-start;
        }
        .three-col-form .profile-meta-row .form-group {
          flex: 1 1 0 !important;
          min-width: 0;
        }
        /* Role holds one short word, so it takes only what "Admin" needs and
           hands the rest of the row to the Start/Top of the Line title */
        .three-col-form .profile-meta-row .form-group:first-child {
          flex: 0 0 72px !important;
          max-width: 72px;
        }
        .three-col-form .profile-meta-row .form-group:nth-child(3) {
          flex: 0 1 180px !important;
          max-width: 190px;
          min-width: 120px;
        }
        .three-col-form .profile-meta-row .form-group:nth-child(2) label {
          white-space: normal;
          overflow: visible;
          text-overflow: unset;
          text-align: center;
        }
        .three-col-form .profile-meta-row label {
          font-size: 14px;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        /* One line, the whole title, no ellipsis: it holds 18px whenever that
           fits and the auto-fit effect steps the size down when it does not. */
        .three-col-form .profile-start-top-value {
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          height: 38px;
          min-height: 38px;
          max-height: 38px;
          padding: 8px 10px;
          border: 1px solid #e0c200;
          border-radius: 6px;
          background: #fff59d;
          color: #333;
          font-size: 18px;
          font-weight: 700;
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          box-sizing: border-box;
        }
        /* Desktop shows the one-line title; the member/spouse split is mobile only */
        .three-col-form .profile-start-top-single {
          min-width: 0;
        }
        .three-col-form .profile-start-top-lines {
          display: none;
        }
        .three-col-form .profile-meta-row .head-of-line-readonly {
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          height: 38px !important;
          min-height: 38px !important;
          max-height: 38px !important;
          padding: 8px 10px !important;
          font-size: 14px;
          line-height: 1.25;
          box-sizing: border-box;
          overflow: hidden;
          min-width: 0;
          border: 1px solid #dcdfe3 !important;
          border-radius: 6px !important;
        }
        .three-col-form .profile-meta-row .head-of-line-readonly > span {
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .three-col-form .form-group {
          flex: 1 1 calc(33.333% - 20px) !important;
          min-width: 0;
          margin-bottom: 0;
        }
        .three-col-form .form-group input,
        .three-col-form .form-group select {
          width: 100%;
          box-sizing: border-box;
        }
        /* One line = same 38px as DropdownSelect (.modal-dropdown-cs) + PhoneInput */
        .seeting-profile .three-col-form .form-group > input:not([type="file"]) {
          height: 38px;
          min-height: 38px;
          max-height: 38px;
          padding: 8px 10px;
          font-size: 14px;
        }
        .seeting-profile .three-col-form .dropdown-selection-cs .modal-dropdown-cs,
        .seeting-profile .three-col-form .dropdown-selection-cs input.modal-dropdown-cs {
          height: 38px !important;
          min-height: 38px !important;
          max-height: 38px !important;
          padding: 8px 10px !important;
          box-sizing: border-box !important;
        }
        .seeting-profile .three-col-form .form-group .react-tel-input {
          width: 100% !important;
          margin-bottom: 0 !important;
        }
        .seeting-profile .three-col-form .form-group .react-tel-input .form-control {
          width: 100% !important;
          height: 38px !important;
          min-height: 38px !important;
          max-height: 38px !important;
          box-sizing: border-box !important;
        }

        /* Edit mode actions: keep Cancel/Save on one line inside the form column
           instead of two fixed 220px buttons that overflowed narrow cards. */
        .seeting-profile .float-inline-end {
          float: none;
          display: block;
          max-width: min(100%, 840px);
          margin: 0 auto;
        }
        /* Cancel/Save centred on the field block (the same 840px format as
           the Special Notes input), flush left in the card like the form. */
        @media (min-width: 769px) {
          .seeting-profile .float-inline-end {
            max-width: min(100%, 840px);
            margin-left: 0;
            margin-right: auto;
          }
        }
        .seeting-profile .button-setting {
          display: flex;
          flex-wrap: nowrap;
          justify-content: flex-end;
          align-items: stretch;
          float: none;
          gap: 12px;
          margin-top: 18px;
        }
        .seeting-profile .button-setting .btn-cancel,
        .seeting-profile .button-setting .btn-save,
        .seeting-profile .button-setting .btn-cancel:hover,
        .seeting-profile .button-setting .btn-save:hover {
          flex: 1 1 0;
          width: auto;
          min-width: 0;
          max-width: 220px;
          padding: 12px 10px;
          border-radius: 12px;
          /* Same reason as .edit-button-cs above: .btn-save carries no .btn
             class, so without this it falls back to the browser UI font while
             Cancel next to it uses the body face. */
          font-family: inherit;
          font-size: 15px;
          font-weight: 600;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        /* Last word on desktop placement: the pair sits centred on the 840px
           field block, so it lines up with the Special Notes input above it
           (the .button-setting rules above end in flex-end, hence this). */
        @media (min-width: 769px) {
          .seeting-profile .button-setting {
            justify-content: center;
          }
          .seeting-profile .button-setting .btn-cancel,
          .seeting-profile .button-setting .btn-save {
            flex: 0 0 220px;
            width: 220px;
          }
        }

        /* Special Notes reads one size smaller than the browser default the
           editor otherwise inherits. Set on the wrapper, not the columns, so
           the off-DOM measurer (font: inherit, see AddMemberSpecialNotesField)
           splits the text at the same size it is drawn at. Scoped to the
           profile card so the Add Member modal keeps its own size. */
        .seeting-profile .addmember-special-notes-editor-wrap {
          font-size: 15px;
        }

        /* ---- Mobile only ----------------------------------------------------
           Desktop above stays exactly as the web version was: three fields per
           row and the one-line Start/Top of the Line title. */
        @media (max-width: 768px) {
          /* The page would not scroll on a phone. .family-tree-container --
             which this settings shell also is -- is a fixed
             height: calc(100vh - 94px) + overflow: auto scroll box, and on
             mobile it sits inside the page pane (.app-layout-content), which is
             already the scroller. Two nested scrollers, the inner one taller
             than the space left under the mobile header, so its own bottom --
             and the end of the form with it -- sat below the screen and could
             not be reached. The shell sizes to its content here and hands the
             scrolling back to the pane. Desktop keeps the fixed-height box.
             This style block only exists while the profile page is mounted, so
             the other settings pages keep their own box. */
          .family-tree-container.settings-shell {
            height: auto;
            min-height: 0;
            overflow: visible;
          }
          .three-col-form .form-group {
            flex: 1 1 100% !important;
            max-width: none !important;
          }
          .three-col-form .profile-meta-row {
            flex-wrap: wrap;
          }
          .three-col-form .profile-meta-row .form-group,
          .three-col-form .profile-meta-row .form-group:first-child,
          .three-col-form .profile-meta-row .form-group:nth-child(3) {
            flex: 1 1 100% !important;
            max-width: none !important;
          }
          .three-col-form .profile-meta-row .form-group:nth-child(2) label {
            text-align: left;
          }
          /* Long names wrap/scroll instead of being clipped */
          .three-col-form .profile-start-top-value {
            height: auto;
            max-height: none;
            justify-content: flex-start;
            text-align: left;
            font-size: 15px;
            white-space: normal;
            overflow: visible;
            text-overflow: clip;
          }
          /* Member on line 1, spouse on line 2 — same colours as the tree node.
             Each name keeps to a single line; the box scrolls if it cannot fit. */
          .three-col-form .profile-start-top-value--has-lines {
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
            font-size: 13px;
          }
          .three-col-form .profile-start-top-value--has-lines .profile-start-top-single {
            display: none;
          }
          .three-col-form .profile-start-top-value--has-lines .profile-start-top-lines {
            display: block;
            width: 100%;
            overflow-x: auto;
          }
          .three-col-form .profile-start-top-line {
            display: block;
            width: max-content;
            max-width: none;
            line-height: 1.35;
            white-space: nowrap;
          }
          .three-col-form .profile-start-top-years {
            padding-left: 4px;
            font-weight: 700;
          }
          .three-col-form .profile-meta-row .head-of-line-readonly {
            height: auto !important;
            max-height: none !important;
            flex-wrap: wrap;
          }
          /* Special Notes is the last field in the card, and the card ended
             well short of the screen bottom. Grow the box down into that gap
             (7 lines instead of the 4-line default in popups.css) so more of
             the note reads without scrolling inside it. Scoped to the profile
             card so the Add Member modal keeps its 4-line box. */
          .seeting-profile .addmember-special-notes-columns {
            max-height: calc(1.45em * 7 + 20px);
            /* The hint is a SIBLING of this box, positioned absolutely against
               the wrapper around them both (see AddMemberSpecialNotesField).
               With the note empty this box collapses to its padding, so the
               7-line hint hung ~120px below the card -- outside .right-side,
               whose overflow-y:auto then turned it into a second scroll box
               stacked under the page. A swipe spent itself in that box before
               the page moved, which is what read as the scroll not working.
               Reserving the hint's height here keeps the box and the hint the
               same size, so nothing overflows and the page is the only
               scroller. */
            min-height: calc(1.45em * 7 + 20px);
          }
          .seeting-profile .addmember-special-notes-placeholder {
            max-height: calc(1.45em * 7);
          }
        }

        @media (max-width: 576px) {
          .family-tree-container.settings-shell .right-side {
            padding: 12px 14px 16px;
          }
          .seeting-profile .edit-button-cs {
            width: auto;
            min-width: 80px;
            padding: 8px 16px;
            font-size: 14px !important;
          }
          /* Keep the transparent upload input over the avatar only */
          .seeting-profile .profile-image-container .input-field-input {
            left: 0;
            width: 100%;
          }
        }
      `}</style>
      <div className="right-side">
        <div className="seeting-profile">
          <form>
            <div className="profile-header text-center position-relative">
              {!isEditable ? (
                <button
                  type="button"
                  className="edit-button-cs"
                  onClick={() => {
                    if (isEditable) handleCancel();
                    else setIsEditable(true);
                  }}
                >
                  <span className={isEditable ? "Cancel Edit" : "Edit Profile"}>
                    Edit{" "}
                  </span>
                </button>
              ) : (
                " "
              )}
              <div className="profile-image-container">
                <button
                  type="button"
                  className="edit-profile-btn"
                  onClick={() => {
                    if (isEditable) {
                      document.getElementById("profile-image-input")?.click();
                    }
                  }}
                >
                  {/* <span className={isEditable ? "Cancel Edit" : "Edit Profile"}>
                    <img src="/assets/images/take-pic.svg" className="camera" />{" "}
                  </span> */}

                  {isEditable ? (
                    <span
                      className={isEditable ? "Cancel Edit" : "Edit Profile"}
                    >
                      <img
                        src={window.getPublicUrl("/assets/images/edit-cion.svg")}
                        className="edit-icon"
                      />{" "}
                    </span>
                  ) : (
                    ""
                  )}
                </button>
                <img
                  src={
                    formData.profilePicPreview ||
                    (typeof formData.profilePic === "string" && formData.profilePic
                      ? formData.profilePic
                      : "") ||
                    window.getPublicUrl("/assets/images/profile-dummy.svg")
                  }
                  alt="Profile"
                  className="profile-pic"
                  onClick={() => {
                    if (isEditable) {
                      document.getElementById("profile-image-input")?.click();
                    }
                  }}
                  style={{ cursor: isEditable ? "pointer" : "default" }}
                />

                {isEditable && (
                  <>
                    <input
                      id="profile-image-input"
                      type="file"
                      className="input-field-input"
                      accept=".png,.jpg,.jpeg"
                      onChange={handleImageChange}
                    />
                  </>
                )}

                {/* <label
            className="border-0 bg-none"
            onClick={() => {
              if (isEditable) handleCancel();
              else setIsEditable(true);
            }}
          >
         {isEditable && (
            <input type="file" accept="image/*" onChange={handleImageChange} />
          )}
           <span className={`fas ${isEditable ? "" : ""}`}><img src="/assets/images/edit-icon-profile.svg" /> </span>
            
          </label> */}
                {/* <i className={`fas ${isEditable ? "fa-times" : "fa-pen"}`}></i> */}
              </div>
            </div>
            <div className="profile-form three-col-form">
              <div className="profile-meta-row">
                <div className="form-group">
                  <label title="Role">Role</label>
                  <input
                    type="text"
                    value={isAdmin ? "Admin" : "User"}
                    readOnly={true}
                  />
                </div>
                <div className="form-group">
                  <label>Start/Top of the Line</label>
                  <div
                    ref={topOfLineRef}
                    className={`profile-start-top-value${
                      topOfLineLines.length
                        ? " profile-start-top-value--has-lines"
                        : ""
                    }`}
                    title={topOfLineTitle}
                  >
                    <span className="profile-start-top-single">
                      {topOfLineTitle}
                    </span>
                    {topOfLineLines.length > 0 && (
                      <div className="profile-start-top-lines">
                        {topOfLineLines.map((line) => (
                          <div className="profile-start-top-line" key={line.key}>
                            {renderNameWithBoldFirst(line.name, line.gender)}
                            {line.years && (
                              <span
                                className="profile-start-top-years"
                                style={{
                                  color:
                                    Number(line.gender) === 1
                                      ? "#0000FF"
                                      : "#FF0000",
                                }}
                              >
                                ({line.years})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label title="Head of Line/Color">Head/Color</label>
                  {headOfLineData ? (
                  <div
                    className="head-of-line-readonly"
                    style={{
                    gap: "4px",
                    backgroundColor: "#fff"
                  }}>
                    <span style={{ color: headOfLineData.head_hex_code || "#000000", flex: "0 1 auto", minWidth: 0 }}>
                      {headOfLineData.head_first_name || ""}
                      {headOfLineData.head_special_name ? ` ${headOfLineData.head_special_name}` : ""}
                      {headOfLineData.head_suffix ? ` ${headOfLineData.head_suffix}` : ""}
                    </span>
                    <span style={{
                      color: headOfLineData.head_hex_code || "#000000",
                      fontSize: "14px",
                      fontWeight: "bold",
                      flex: "0 0 auto"
                    }}>•</span>
                    <span style={{ color: headOfLineData.head_hex_code || "#000000", flex: "0 0 auto" }}>
                      {headOfLineData.head_color_name || ""} Line
                    </span>
                  </div>
                ) : (
                  <div
                    className="head-of-line-readonly"
                    style={{
                    backgroundColor: "#f5f5f5",
                    color: "#999"
                  }}>
                    Loading...
                  </div>
                )}
                </div>
              </div>

              <div className="form-group">
                <label>Bio Descendant Parent</label>
                <input
                  type="text"
                  className ='Bio-descendant-parent'
                  name="bioDescendantParent"
                  value={formData.bioDescendantParent}
                  onChange={handleChange}
                  placeholder="Include both parents if this is Start/Top of the Line only."
                  readOnly={true}
                />
              </div>

          
             
              <div className="form-group">
                <label className="required-label">First Name</label>
                <>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    readOnly={!isEditable}
                  />
                  {errors.firstName && (
                    <span className="error">{errors.firstName}</span>
                  )}
                </>
              </div>
              <div className="form-group">
                <label className="required-label">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  readOnly={!isEditable}
                />
                {errors.lastName && (
                  <span className="error">{errors.lastName}</span>
                )}
              </div>
              <div className="form-group">
                <label className="required-label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="required-label">Cell Type</label>
                <DropdownSelect
                  name="cellType"
                  inputId="city"
                  options={[
                    { value: "1", label: "iPhone" },
                    { value: "2", label: "Android" },
                  ]}
                  value={formData.cellType}
                  onChange={(val) => {
                    if(!isEditable) return;
                    setFormData((prev) => ({...prev, cellType: val}));
                    setErrors((prev) => ({...prev, cellType: ""}));
                  }}
                  placeholder="Cell Type"
                  disabled={!isEditable}
                  maxMenuHeight={100}
                />
                {errors.cellType && (
                  <span className="error">{errors.cellType}</span>
                )}
              </div>

              <div className="form-group">
                <label className="required-label">Cell Number</label>
                {/* <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              readOnly={!isEditable}
            /> */}
                {isEditable ? (
                  <PhoneInput
                    country={
                      formData.countryCode
                        ? formData.countryCode.replace("+", "").toLowerCase()
                        : "us"
                    }
                    value={`${formData.countryCode}${formData.phone}`.replace(
                      /\s+/g,
                      ""
                    )}
                    onChange={handlePhoneChange}
                    inputStyle={{
                      width: "100%",
                      height: "38px",
                      paddingLeft: "48px",
                    }}
                    containerStyle={{
                      marginBottom: "15px",
                    }}
                    inputProps={{
                      name: "phone",
                      required: false,
                    }}
                  />
                ) : (
                  <input
                    type="text"
                    name="phone"
                    value={formatPhoneNumber(formData.countryCode, formData.phone)}
                    readOnly
                  />
                )}
                {errors.phone && <span className="error">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label className="required-label">State</label>
                <DropdownSelect
                  name="city"
                  options={states}
                  value={formData.city}
                  onChange={(val) => {
                    if(!isEditable) return;
                    setFormData((prev) => ({ ...prev, city: val }));
                    setErrors((prev) => ({ ...prev, city: "" }));
                  }}
                  placeholder="State"
                  maxMenuHeight={180}
                  required={true}
                  disabled={!isEditable}
                />
                {errors.city && <span className="error">{errors.city}</span>}
              </div>

              <div className="form-group">
                <label className="required-label">Country</label>
                <DropdownSelect
                  name="country"
                  options={[{ value: "USA", label: "USA" }]}
                  value={formData.country}
                  onChange={(val) => {
                    if(!isEditable) return;
                    setFormData((prev) => ({...prev, country: val}));
                    setErrors((prev) => ({...prev, country: ""}));
                  }}
                  disabled={!isEditable}
                  maxMenuHeight={50}
                  placeholder="Country"
                />
                {errors.country && (
                  <span className="error">{errors.country}</span>
                )}
              </div>

              <div className="form-group">
                <label className="required-label">Marital Status</label>
                <>
                  <input
                    type="text"
                    name="maritalStatus"
                    value={
                      formData.maritalStatus
                        ? maritalStatusOptions.find(
                            (option) => option.value === parseInt(formData.maritalStatus)
                          )?.label || ""
                        : ""
                    }
                    readOnly
                  />
                  {errors.maritalStatus && (
                    <span className="error">{errors.maritalStatus}</span>
                  )}
                </>
              </div>
              {formData.maritalStatus === 2 && (
                <div className="form-group">
                  <label className="required-label">Wedding Anniversary</label>
                  <input
                    type="date"
                    name="weddingAnniversary"
                    value={formData.weddingAnniversary}
                    onChange={handleChange}
                    readOnly={!isEditable}
                  />
                  {
                    errors.weddingAnniversary && (
                      <span className="error">{errors.weddingAnniversary}</span>
                    )
                  }
                </div>
              )}

              <div className="form-group full">
                <label>Special Notes</label>
                <AddMemberSpecialNotesField
                  name="about"
                  value={formData.about}
                  onChange={handleChange}
                  disabled={!isEditable}
                />
              </div>

              {/* <div className="form-group">
                <label>Relation to Head of Line</label>
                <input
                  type="text"
                  name="relationToHeadOfLine"
                  value={formData.relationToHeadOfLine}
                  onChange={handleChange}
                  readOnly={!isEditable}
                />
              </div> */}



            </div>
            {isEditable && (
              <div className="float-inline-end">
                <div className="button-setting">
                  <button
                    className="btn btn-cancel"
                    type="button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    className="save-btn btn-save"
                    type="button"
                    onClick={handleSave}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default MyProfile;
